// @flow

import React, { type Node } from 'react';

import * as backends from './backends';

import type {
  ApiKeys,
  HostedFieldAdditionalData,
  HostedFieldBackendInterface,
  HostedFieldToken,
  HostedFieldType,
  HostedFieldValue,
  PaymentSourceBackend,
} from '@zapier/payment/src/types';

type Props = {
  /** Payment backend API keys */
  apiKeys: ApiKeys,
  /** Payment backend to use */
  backend: PaymentSourceBackend,
  children: (
    createToken: (
      additionalData: HostedFieldAdditionalData
    ) => Promise<HostedFieldToken>
  ) => Node,
  /** Call when field changes */
  onChange: (name: HostedFieldType, value: HostedFieldValue) => void,
};

type State = {
  backend: ?HostedFieldBackendInterface,
};

const createBackend = (
  apiKeys: ApiKeys,
  backend: PaymentSourceBackend
): HostedFieldBackendInterface => backends[backend](apiKeys[backend]);

const HostedFieldStyle = {
  // Custom font only works with Stripe for now as Braintree does not support it.
  // I've revived an issue on their side as technically they could support it but their
  // justification is vague:
  //   https://github.com/braintree/braintree-web/issues/75#issuecomment-441748583
  fontFamily: '"Open Sans", Helvetica, arial, sans-serif',
  fontSize: '14px',
  color: '#5f6c72',
  '::placeholder': {
    color: '#969ea2',
  },
};

export default class HostedFieldsProvider extends React.Component<
  Props,
  State
> {
  state = {
    backend: null,
  };

  async componentDidMount() {
    const backend = await createBackend(this.props.apiKeys, this.props.backend);

    try {
      await backend.createFields(HostedFieldStyle, this.props.onChange);
    } catch (error) {
      // If the user switches to another page or to Paypal quickly
      // an error may be thrown here because the DOM elements won't exist anymore.
      // In that case we silently fail here.
      return;
    }

    this.setState({ backend });
  }

  componentWillUnmount() {
    const { state } = this;
    if (state.backend) {
      state.backend.destroyFields();
    }
  }

  render() {
    const { props, state } = this;
    const createToken = (
      additionalData: HostedFieldAdditionalData
    ): Promise<HostedFieldToken> => {
      if (!state.backend) throw new Error('Payment provider is not ready.');
      return state.backend.createToken(additionalData);
    };

    return props.children(createToken);
  }
}
